import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getCourseLevelLabel } from "../../../constants/courseLevels";
import TeacherNavbar from "./TeacherNavbar";
import MessageCard from "../../MessageCard/MessageCard";

import { fetchCourseTree } from "../../../redux/Actions/courseTreeAction";
import {
  fetchTeachersWithAccess,
  createTeacherCourseByManager,
  fetchTeacherManagerCourses,
  updateTeacherCourseByManager,
  clearTeacherManagerTeachersError,
  clearTeacherManagerCreateError,
  clearTeacherManagerCoursesError,
  clearTeacherManagerCreateSuccess,
  clearTeacherManagerUpdateState,
} from "../../../redux/Actions/teacherManagerCourseActions";

const weekdays = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const TeacherManagerCoursesPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const profileName = useSelector((s) => s.auth.user.profileName);
  const roleName = useSelector((s) => s.auth.user.roleName);

  const { treeData } = useSelector((s) => s.courseTree);

  const {
    teachers,
    teachersLoading,
    teachersError,
    createLoading,
    createSuccess,
    createdCourse,
    createError,
    courses,
    coursesLoading,
    coursesError,
    updateLoading,
    updateSuccess,
    updateMessage,
    updateError,
  } = useSelector((s) => s.teacherManagerCourses);

  const [tab, setTab] = useState(0);

  // -------- Create form state
  const [teacherId, setTeacherId] = useState("");
  const [parentTitle, setParentTitle] = useState("");
  const [courseType, setCourseType] = useState("Normal");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [audienceType, setAudienceType] = useState("");
  const [minimumAge, setMinimumAge] = useState("");
  const [maximumAge, setMaximumAge] = useState("");
  const [dayTimes, setDayTimes] = useState([{ dayOfWeek: "", startTime: "", endTime: "" }]);

  const [localMsg, setLocalMsg] = useState(null);
  const [localMsgType, setLocalMsgType] = useState("info");

  const [coursesView, setCoursesView] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [editDraft, setEditDraft] = useState({
    parentTitle: "",
    courseTitle: "",
    startDate: "",
    endDate: "",
    duration: "",
    gNumber: "",
    audienceType: "",
    minimumAge: "",
    maximumAge: "",
  });
  const [editDayTimes, setEditDayTimes] = useState([
    { dayOfWeek: "", startTime: "", endTime: "" },
  ]);

  useEffect(() => {
    dispatch(fetchTeachersWithAccess());
    dispatch(fetchCourseTree());
  }, [dispatch]);

  useEffect(() => {
    if (tab === 1) {
      dispatch(fetchTeacherManagerCourses());
    }
  }, [dispatch, tab]);

  useEffect(() => {
    setCoursesView(Array.isArray(courses) ? courses : []);
  }, [courses]);

  useEffect(() => {
    if (!createSuccess) return;
    setLocalMsg(null);
    // reset form
    setParentTitle("");
    setCourseType("Normal");
    setStartDate("");
    setEndDate("");
    setAudienceType("");
    setMinimumAge("");
    setMaximumAge("");
    setDayTimes([{ dayOfWeek: "", startTime: "", endTime: "" }]);
  }, [createSuccess]);

  const parentTitles = useMemo(() => (treeData || []).map((c) => c.title), [treeData]);
  const levelOptions = parentTitles;

  const handleDayTimeChange = (index, field, value) => {
    setDayTimes((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddDay = () => {
    setDayTimes((prev) => [...prev, { dayOfWeek: "", startTime: "", endTime: "" }]);
  };

  const handleRemoveDay = (index) => {
    setDayTimes((prev) => prev.filter((_, i) => i !== index));
  };

  const timeToMinutes = (value) => {
    const [h, m] = value.split(":").map(Number);
    return h * 60 + m;
  };

  const normalizeDayTimes = () =>
    dayTimes
      .filter((dt) => dt.dayOfWeek && dt.startTime && dt.endTime)
      .map((dt) => ({
        dayOfWeek: dt.dayOfWeek,
        startTime: dt.startTime,
        endTime: dt.endTime,
      }));

  const validate = () => {
    if (!teacherId) return "Please select a teacher.";
    if (!parentTitle) return "Please select a course level.";
    if (!startDate || !endDate) return "Please select start and end dates.";
    if ((minimumAge === "") !== (maximumAge === "")) return "Enter both minimum and maximum age, or leave both empty.";
    if (minimumAge !== "" && (Number(minimumAge) < 0 || Number(maximumAge) > 120 || Number(minimumAge) > Number(maximumAge))) return "Enter a valid age range between 0 and 120.";
    const normalized = normalizeDayTimes();
    if (!normalized.length) return "Please add at least one schedule entry.";

    const daySet = new Set();
    for (const dt of normalized) {
      if (daySet.has(dt.dayOfWeek)) return "Duplicate days are not allowed.";
      daySet.add(dt.dayOfWeek);

      if (timeToMinutes(dt.endTime) <= timeToMinutes(dt.startTime))
        return `End time must be after start time for ${dt.dayOfWeek}.`;
    }
    return null;
  };

  const handleCreate = () => {
    setLocalMsg(null);

    const err = validate();
    if (err) {
      setLocalMsg(err);
      setLocalMsgType("error");
      return;
    }

    const normalized = normalizeDayTimes();

    const daysPerWeek = normalized.length;

    const startTime = normalized.reduce((min, dt) => {
      return timeToMinutes(dt.startTime) < timeToMinutes(min) ? dt.startTime : min;
    }, normalized[0].startTime);

    const endTime = normalized.reduce((max, dt) => {
      return timeToMinutes(dt.endTime) > timeToMinutes(max) ? dt.endTime : max;
    }, normalized[0].endTime);

    const payload = {
      teacherId: Number(teacherId),
      courseTitle: parentTitle,
      parentTitle,
      courseType,
      daysPerWeek,
      startDate,
      endDate,
      startTime,
      endTime,
      audienceType: audienceType || null,
      minimumAge: minimumAge === "" ? null : Number(minimumAge),
      maximumAge: maximumAge === "" ? null : Number(maximumAge),
      dayTimes: normalized,
    };

    dispatch(createTeacherCourseByManager(payload));
  };

  const createDisabled = createLoading || Boolean(validate());

  const safeCourses = Array.isArray(coursesView) ? coursesView : [];

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
  };

  const formatTeacher = (row) => {
    const name = [row.teacherFirstName, row.teacherLastName]
      .filter(Boolean)
      .join(" ");
    return row.teacherName || name || row.teacherEmail || row.teacherId || "-";
  };

  const getRawGroupNumber = (row) =>
    row?.gNumber ?? row?.gnumber ?? row?.g_number ?? row?.groupNumber ?? null;

  const formatGroupNumber = (value) => {
    if (!value) return "-";
    if (typeof value === "string") return value;
    if (Array.isArray(value)) return String.fromCharCode(...value);
    if (Array.isArray(value.value)) return String.fromCharCode(...value.value);
    if (Array.isArray(value.bytes)) return String.fromCharCode(...value.bytes);
    if (Array.isArray(value.data)) return String.fromCharCode(...value.data);
    if (Array.isArray(value?.value?.data)) return String.fromCharCode(...value.value.data);
    return String(value);
  };

  const formatTime = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value.slice(0, 5);
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toTimeString().slice(0, 5);
  };

  const formatSchedule = (row) => {
    if (Array.isArray(row.dayTimes) && row.dayTimes.length) {
      return row.dayTimes
        .map((dt) => {
          const day = dt.dayOfWeek || "";
          const start = formatTime(dt.startTime);
          const end = formatTime(dt.endTime);
          return `${day} ${start}-${end}`.trim();
        })
        .join(", ");
    }
    if (Array.isArray(row.daysOfWeek) && row.daysOfWeek.length) {
      return row.daysOfWeek.join(", ");
    }
    if (row.startTime && row.endTime) {
      return `${formatTime(row.startTime)}-${formatTime(row.endTime)}`;
    }
    return "-";
  };

  const normalizeEditDayTimes = (times) =>
    times
      .filter((dt) => dt.dayOfWeek && dt.startTime && dt.endTime)
      .map((dt) => ({
        dayOfWeek: dt.dayOfWeek,
        startTime: dt.startTime,
        endTime: dt.endTime,
      }));

  const buildEditDayTimes = (row) => {
    if (Array.isArray(row.dayTimes) && row.dayTimes.length) {
      return row.dayTimes.map((dt) => ({
        dayOfWeek: dt.dayOfWeek || "",
        startTime: formatTime(dt.startTime),
        endTime: formatTime(dt.endTime),
      }));
    }
    if (Array.isArray(row.daysOfWeek) && row.daysOfWeek.length) {
      const start = formatTime(row.startTime);
      const end = formatTime(row.endTime);
      return row.daysOfWeek.map((day) => ({
        dayOfWeek: day,
        startTime: start,
        endTime: end,
      }));
    }
    return [{ dayOfWeek: "", startTime: "", endTime: "" }];
  };

  const toInputDate = (value) => {
    if (!value) return "";
    if (typeof value === "string" && value.length >= 10) return value.slice(0, 10);
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
  };

  const handleOpenEdit = (row, index) => {
    setEditRow(row);
    setEditIndex(index);
    setEditDraft({
      parentTitle: row.parentTitle || row.parentCourseTitle || "",
      courseTitle: row.courseTitle || row.childTitle || row.title || "",
      startDate: toInputDate(row.startDate),
      endDate: toInputDate(row.endDate),
      duration: row.duration ?? "",
      gNumber: formatGroupNumber(getRawGroupNumber(row)),
      audienceType: row.audienceType || "",
      minimumAge: row.minimumAge ?? "",
      maximumAge: row.maximumAge ?? "",
    });
    setEditDayTimes(buildEditDayTimes(row));
    setEditOpen(true);
    if (updateSuccess || updateError || updateMessage) {
      dispatch(clearTeacherManagerUpdateState());
    }
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditRow(null);
    setEditIndex(null);
    setEditDayTimes([{ dayOfWeek: "", startTime: "", endTime: "" }]);
  };

  useEffect(() => {
    if (!updateSuccess) return;
    dispatch(fetchTeacherManagerCourses());
    handleCloseEdit();
  }, [dispatch, updateSuccess]);

  const editAgeRangeInvalid =
    (editDraft.minimumAge === "") !== (editDraft.maximumAge === "") ||
    (editDraft.minimumAge !== "" &&
      (Number(editDraft.minimumAge) < 0 ||
        Number(editDraft.maximumAge) > 120 ||
        Number(editDraft.minimumAge) > Number(editDraft.maximumAge)));

  const handleSaveEdit = () => {
    if (editIndex === null || editAgeRangeInvalid) return;
    const normalizedDayTimes = normalizeEditDayTimes(editDayTimes);
    const daysOfWeek = normalizedDayTimes.map((dt) => dt.dayOfWeek);
    const startTime = normalizedDayTimes.length
      ? normalizedDayTimes.reduce((min, dt) => {
          return timeToMinutes(dt.startTime) < timeToMinutes(min) ? dt.startTime : min;
        }, normalizedDayTimes[0].startTime)
      : editRow?.startTime;
    const endTime = normalizedDayTimes.length
      ? normalizedDayTimes.reduce((max, dt) => {
          return timeToMinutes(dt.endTime) > timeToMinutes(max) ? dt.endTime : max;
        }, normalizedDayTimes[0].endTime)
      : editRow?.endTime;
    setCoursesView((prev) =>
      prev.map((item, index) =>
        index === editIndex
          ? {
              ...item,
              parentTitle: editDraft.courseTitle,
              courseTitle: editDraft.courseTitle,
              startDate: editDraft.startDate,
              endDate: editDraft.endDate,
              duration: editDraft.duration,
              gNumber: editDraft.gNumber,
              dayTimes: normalizedDayTimes,
              daysOfWeek,
              daysPerWeek: normalizedDayTimes.length || item.daysPerWeek,
              startTime,
              endTime,
              audienceType: editDraft.audienceType,
              minimumAge: editDraft.minimumAge,
              maximumAge: editDraft.maximumAge,
            }
          : item
      )
    );
    if (editRow?.teacherCourseId || editRow?.id) {
      const payload = {
        courseTitle: editDraft.courseTitle,
        startDate: editDraft.startDate,
        endDate: editDraft.endDate,
        dayTimes: normalizedDayTimes,
        daysPerWeek: normalizedDayTimes.length,
        daysOfWeek,
        startTime,
        endTime,
        audienceType: editDraft.audienceType || null,
        minimumAge: editDraft.minimumAge === "" ? null : Number(editDraft.minimumAge),
        maximumAge: editDraft.maximumAge === "" ? null : Number(editDraft.maximumAge),
      };
      dispatch(updateTeacherCourseByManager(editRow.teacherCourseId || editRow.id, payload));
    }
  };

  const editDisabled =
    !editDraft.courseTitle ||
    !editDraft.startDate ||
    !editDraft.endDate ||
    !normalizeEditDayTimes(editDayTimes).length ||
    editAgeRangeInvalid;

  const handleEditDayTimeChange = (index, field, value) => {
    setEditDayTimes((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleEditAddDay = () => {
    setEditDayTimes((prev) => [...prev, { dayOfWeek: "", startTime: "", endTime: "" }]);
  };

  const handleEditRemoveDay = (index) => {
    setEditDayTimes((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ display: "flex" }}>
      <TeacherNavbar profileName={profileName} roleName={roleName} />

      <Box
        sx={{
          ml: { xs: 0, md: "250px" },
          px: 6,
          py: 3,
          width: { xs: "100%", md: "calc(100% - 250px)" },
          bgcolor: "#f0f7ff",
          height: "100vh",
          overflowY: "auto",
        }}
      >
        <Box sx={{ bgcolor: "white", p: 3, borderRadius: 2, boxShadow: 3 }}>
          <Typography variant="h4" sx={{ color: "#0077b6", fontWeight: "bold", mb: 2 }}>
            Teacher Manager • Courses
          </Typography>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Create Course for Teacher" />
            <Tab label="Manage Courses" />
          </Tabs>

          <Divider sx={{ mb: 2 }} />

          {/* ---------------- TAB 1: CREATE ---------------- */}
          {tab === 0 && (
            <>
              {(teachersLoading || !teachers) && <CircularProgress />}

              {teachersError && (
                <MessageCard
                  message={teachersError}
                  type="error"
                  onClose={() => dispatch(clearTeacherManagerTeachersError())}
                />
              )}

              {localMsg && (
                <MessageCard message={localMsg} type={localMsgType} onClose={() => setLocalMsg(null)} />
              )}

              {createError && (
                <MessageCard
                  message={createError}
                  type="error"
                  onClose={() => dispatch(clearTeacherManagerCreateError())}
                />
              )}

              {createSuccess && (
                <MessageCard
                  message={
                    createdCourse?.message ||
                    `Created: ${createdCourse?.title || parentTitle}`
                  }
                  type="success"
                  onClose={() => dispatch(clearTeacherManagerCreateSuccess())}
                />
              )}

              <Grid container spacing={3}>
                {/* Teacher select */}
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Select Teacher"
                    value={teacherId}
                    onChange={(e) => setTeacherId(e.target.value)}
                  >
                    {(teachers || []).map((t) => (
                      <MenuItem key={t.teacherId} value={t.teacherId}>
                        {t.firstName} {t.lastName} — {t.email}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Parent */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Course Level"
                    value={parentTitle}
                    onChange={(e) => setParentTitle(e.target.value)}
                  >
                    {parentTitles.map((title) => (
                      <MenuItem key={title} value={title}>
                        {getCourseLevelLabel(t, title)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Dates */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField select fullWidth label="Audience (optional)" value={audienceType} onChange={(e) => setAudienceType(e.target.value)}>
                    <MenuItem value="">Not specified</MenuItem>
                    <MenuItem value="ADULTS">Adults</MenuItem>
                    <MenuItem value="CHILDREN">Children</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Minimum age (optional)" type="number" value={minimumAge} onChange={(e) => setMinimumAge(e.target.value)} inputProps={{ min: 0, max: 120 }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Maximum age (optional)" type="number" value={maximumAge} onChange={(e) => setMaximumAge(e.target.value)} inputProps={{ min: 0, max: 120 }} />
                </Grid>

                {/* Schedule */}
                <Grid item xs={12}>
                  <Typography sx={{ fontWeight: 700, mb: 1 }}>Schedule (per day)</Typography>
                </Grid>

                {dayTimes.map((dt, index) => (
                  <Grid item xs={12} key={`${dt.dayOfWeek}-${index}`}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <TextField
                          select
                          fullWidth
                          label="Day"
                          value={dt.dayOfWeek}
                          onChange={(e) => handleDayTimeChange(index, "dayOfWeek", e.target.value)}
                        >
                          {weekdays.map((d) => (
                            <MenuItem key={d} value={d}>
                              {d}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          label="Start Time"
                          type="time"
                          value={dt.startTime}
                          onChange={(e) => handleDayTimeChange(index, "startTime", e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                        />
                      </Grid>

                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          label="End Time"
                          type="time"
                          value={dt.endTime}
                          onChange={(e) => handleDayTimeChange(index, "endTime", e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                        />
                      </Grid>

                      <Grid item xs={12} md={2}>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleRemoveDay(index)}
                          disabled={dayTimes.length === 1}
                          sx={{ width: "100%" }}
                        >
                          Remove
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <Button variant="outlined" onClick={handleAddDay}>
                    Add Day
                  </Button>
                </Grid>
              </Grid>

              <Box mt={3} display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  onClick={handleCreate}
                  disabled={createDisabled}
                >
                  {createLoading ? "Creating..." : "Create Course"}
                </Button>
              </Box>
            </>
          )}

          {/* ---------------- TAB 2: MANAGE (later) ---------------- */}
          {tab === 1 && (
            <>
              {coursesLoading && <CircularProgress />}

              {coursesError && (
                <MessageCard
                  message={coursesError}
                  type="error"
                  onClose={() => dispatch(clearTeacherManagerCoursesError())}
                />
              )}
              {updateError && (
                <MessageCard
                  message={updateError}
                  type="error"
                  onClose={() => dispatch(clearTeacherManagerUpdateState())}
                />
              )}
              {updateSuccess && updateMessage && (
                <MessageCard
                  message={updateMessage}
                  type="success"
                  onClose={() => dispatch(clearTeacherManagerUpdateState())}
                />
              )}

              {!coursesLoading && safeCourses.length === 0 && (
                <Typography color="text.secondary">No courses available.</Typography>
              )}

              {safeCourses.length > 0 && (
                <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#F7F3DE" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Teacher</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Level</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Group</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Duration (weeks)</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Schedule</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Start Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>End Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {safeCourses.map((row, index) => (
                        <TableRow
                          key={
                            row.id ||
                            row.teacherCourseId ||
                            row.courseId ||
                            `${row.courseTitle || row.title}-${index}`
                          }
                          sx={{
                            "&:nth-of-type(odd)": { backgroundColor: "rgba(0,0,0,0.02)" },
                            "&:hover": { backgroundColor: "rgba(240, 192, 67, 0.12)" },
                          }}
                        >
                          <TableCell>{formatTeacher(row)}</TableCell>
                          <TableCell>{row.courseTitle || row.title || "-"}</TableCell>
                          <TableCell>{formatGroupNumber(getRawGroupNumber(row))}</TableCell>
                          <TableCell>{row.courseType || row.type || "-"}</TableCell>
                          <TableCell>{row.duration ?? "-"}</TableCell>
                          <TableCell>{formatSchedule(row)}</TableCell>
                          <TableCell>{formatDate(row.startDate)}</TableCell>
                          <TableCell>{formatDate(row.endDate)}</TableCell>
                          <TableCell>{row.statusName || row.status || "-"}</TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleOpenEdit(row, index)}
                            >
                              Update
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          <Dialog open={editOpen} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
            <DialogTitle>Update Course</DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Teacher"
                    value={formatTeacher(editRow || {})}
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Current Level"
                    value={editRow?.courseTitle || editRow?.title || "-"}
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Status"
                    value={editRow?.statusName || editRow?.status || "-"}
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Type"
                    value={editRow?.courseType || editRow?.type || "-"}
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Course Level"
                    value={editDraft.courseTitle}
                    onChange={(e) =>
                      setEditDraft((prev) => ({ ...prev, courseTitle: e.target.value }))
                    }
                  >
                    {levelOptions.map((child) => (
                      <MenuItem key={child} value={child}>
                        {getCourseLevelLabel(t, child)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Group Number"
                    value={editDraft.gNumber}
                    disabled
                    onChange={(e) =>
                      setEditDraft((prev) => ({ ...prev, gNumber: e.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={editDraft.startDate}
                    onChange={(e) =>
                      setEditDraft((prev) => ({ ...prev, startDate: e.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={editDraft.endDate}
                    onChange={(e) =>
                      setEditDraft((prev) => ({ ...prev, endDate: e.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Duration (weeks)"
                    type="number"
                    value={editDraft.duration}
                    disabled
                    onChange={(e) =>
                      setEditDraft((prev) => ({ ...prev, duration: e.target.value }))
                    }
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField select fullWidth label="Audience (optional)" value={editDraft.audienceType} onChange={(e) => setEditDraft((prev) => ({ ...prev, audienceType: e.target.value }))}>
                    <MenuItem value="">Not specified</MenuItem>
                    <MenuItem value="ADULTS">Adults</MenuItem>
                    <MenuItem value="CHILDREN">Children</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Minimum age (optional)" type="number" value={editDraft.minimumAge} onChange={(e) => setEditDraft((prev) => ({ ...prev, minimumAge: e.target.value }))} inputProps={{ min: 0, max: 120 }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Maximum age (optional)" type="number" value={editDraft.maximumAge} onChange={(e) => setEditDraft((prev) => ({ ...prev, maximumAge: e.target.value }))} inputProps={{ min: 0, max: 120 }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ fontWeight: 700, mt: 1 }}>
                    Schedule (per day)
                  </Typography>
                </Grid>
                {editDayTimes.map((dt, index) => (
                  <Grid item xs={12} key={`${dt.dayOfWeek}-${index}`}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <TextField
                          select
                          fullWidth
                          label="Day"
                          value={dt.dayOfWeek}
                          onChange={(e) =>
                            handleEditDayTimeChange(index, "dayOfWeek", e.target.value)
                          }
                        >
                          {weekdays.map((d) => (
                            <MenuItem key={d} value={d}>
                              {d}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          label="Start Time"
                          type="time"
                          value={dt.startTime}
                          onChange={(e) =>
                            handleEditDayTimeChange(index, "startTime", e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                        />
                      </Grid>

                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          label="End Time"
                          type="time"
                          value={dt.endTime}
                          onChange={(e) =>
                            handleEditDayTimeChange(index, "endTime", e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                        />
                      </Grid>

                      <Grid item xs={12} md={2}>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleEditRemoveDay(index)}
                          disabled={editDayTimes.length === 1}
                          sx={{ width: "100%" }}
                        >
                          Remove
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <Button variant="outlined" onClick={handleEditAddDay}>
                    Add Day
                  </Button>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseEdit}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSaveEdit}
                disabled={editDisabled || updateLoading}
              >
                {updateLoading ? "Saving..." : "Save Changes"}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Box>
  );
};

export default TeacherManagerCoursesPage;






